<template>
  <div class="sentence-editor">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <a-spin size="large">
        <template #indicator>
          <LoadingOutlined style="font-size: 24px" spin />
        </template>
      </a-spin>
      <p>正在加载文档内容...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <a-result
        status="error"
        title="内容加载失败"
        :sub-title="error"
      >
        <template #extra>
          <a-button type="primary" @click="$emit('reload')">
            重新加载
          </a-button>
        </template>
      </a-result>
    </div>

    <!-- 句子列表 -->
    <div v-else class="sentences-container" ref="sentencesContainer">
      <div 
        v-for="(sentence, index) in sentences" 
        :key="sentence.id"
        class="sentence-pair"
        :class="{ 
          highlighted: highlightedIndex === index,
          modified: sentence.is_modified,
          editing: editingIndex === index
        }"
        :data-sentence-id="sentence.id"
        @click="handleSentenceClick(index)"
      >
        <!-- 句子编号 -->
        <div class="sentence-number">{{ index + 1 }}</div>
        
        <!-- 原文 -->
        <div class="sentence-source">
          <div class="sentence-text">{{ sentence.source_text }}</div>
        </div>
        
        <!-- 译文 -->
        <div class="sentence-target">
          <div 
            v-if="editable && editingIndex === index"
            class="sentence-editor"
            contenteditable="true"
            @input="handleEdit(sentence.id, $event)"
            @blur="handleBlur(sentence.id)"
            @keydown="handleKeydown($event, sentence.id)"
            :ref="el => editingElement = el as HTMLElement"
          ></div>
          <div 
            v-else
            class="sentence-text"
            :class="{ 'clickable': editable }"
            @click="editable && startEdit(index)"
            v-html="sentence.target_text"
          ></div>
          
          <!-- 修改状态指示器 -->
          <div v-if="sentence.is_modified" class="modification-indicator">
            <a-tag size="small" color="orange">已修改</a-tag>
          </div>
        </div>
        
        <!-- 操作按钮 -->
        <div v-if="editable" class="sentence-actions">
          <!-- 非编辑状态：显示编辑按钮 -->
          <a-button 
            v-if="editingIndex !== index"
            type="text" 
            size="small"
            @click.stop="startEdit(index)"
            title="编辑"
          >
            <EditOutlined />
          </a-button>
          
          <!-- 编辑状态：显示保存和取消按钮 -->
          <template v-if="editingIndex === index">
            <a-button 
              type="text" 
              size="small"
              @click.stop="saveEdit(sentence.id)"
              title="保存"
            >
              <CheckOutlined />
            </a-button>
            <a-button 
              type="text" 
              size="small"
              @click.stop="cancelEdit(sentence.id)"
              title="取消"
            >
              <CloseOutlined />
            </a-button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'
import { message } from 'ant-design-vue'
import { 
  LoadingOutlined, 
  EditOutlined, 
  CheckOutlined, 
  CloseOutlined 
} from '@ant-design/icons-vue'

interface Sentence {
  id: number
  sentence_order: number
  sentence_type: 'text' | 'table'
  source_text: string
  target_text: string
  original_target_text?: string  // 原始译文，用于取消时恢复
  is_modified: boolean
  modified_at?: string
}

interface Props {
  sentences: Sentence[]
  editable?: boolean
  highlightedIndex?: number
  loading?: boolean
  error?: string
}

const props = withDefaults(defineProps<Props>(), {
  editable: false,
  highlightedIndex: -1,
  loading: false,
  error: ''
})

const emit = defineEmits<{
  update: [data: { sentence_id: number; target_text: string }]
  edit: [data: { id: number; text: string; modified: boolean }]
  save: [data: { id: number; text: string }]
  cancel: [id: number]
  click: [index: number]
  scroll: [scrollTop: number]
  reload: []
}>()

// 状态管理
const editingIndex = ref(-1)
const editingElement = ref<HTMLElement>()
const sentencesContainer = ref<HTMLElement>()
// 保存编辑前的原始文本，用于取消时恢复
const originalTextBeforeEdit = ref<string>('')
// 记录 blur 触发的延迟保存定时器，取消时需要清除以避免误保存
const blurSaveTimeout = ref<NodeJS.Timeout | null>(null)

// 监听高亮变化
watch(() => props.highlightedIndex, (newIndex) => {
  if (newIndex >= 0 && newIndex < props.sentences.length) {
    scrollToSentence(newIndex)
  }
})

// 方法实现
const handleSentenceClick = (index: number) => {
  emit('click', index)
  
  // 只有当不在编辑状态时才自动进入编辑模式
  if (props.editable && editingIndex.value !== index && editingIndex.value < 0) {
    startEdit(index)
  }
}

const startEdit = async (index: number) => {
  if (!props.editable) return
  
  // 保存当前编辑的内容
  if (editingIndex.value >= 0) {
    await saveCurrentEdit()
  }
  
  const sentence = props.sentences[index]
  
  // 保存编辑前的原始文本，用于取消时恢复
  originalTextBeforeEdit.value = sentence.target_text || ''
  console.log('保存编辑前文本:', originalTextBeforeEdit.value)
  
  // 切换到编辑模式
  editingIndex.value = index
  
  // 等待DOM更新
  await nextTick()
  
  console.log('startEdit - editingIndex:', editingIndex.value, 'sentence:', sentence)
  
  // 聚焦到编辑元素并设置内容
  if (editingElement.value) {
    const initialText = sentence.target_text || ''
    console.log('设置初始文本:', initialText)
    
    // 设置内容
    editingElement.value.textContent = initialText
    
    // 再次等待，确保内容已设置
    await nextTick()
    
    // 聚焦并移动光标到末尾
    editingElement.value.focus()
    
    // 将光标放到文本末尾
    const range = document.createRange()
    const selection = window.getSelection()
    
    if (editingElement.value.childNodes.length > 0) {
      range.selectNodeContents(editingElement.value)
      range.collapse(false)
    } else {
      // 如果没有文本节点，创建一个
      const textNode = document.createTextNode('')
      editingElement.value.appendChild(textNode)
      range.setStart(textNode, 0)
      range.setEnd(textNode, 0)
    }
    
    selection?.removeAllRanges()
    selection?.addRange(range)
    
    console.log('编辑框已聚焦，内容长度:', editingElement.value.textContent?.length)
  }
}

// 防抖保存机制（百度翻译模式）
const pendingSaves = ref<Map<number, NodeJS.Timeout>>(new Map())

const handleEdit = (sentenceId: number, event: Event) => {
  const target = event.target as HTMLElement
  const newText = target.innerText || target.textContent || ''
  
  // 更新本地状态
  const sentence = props.sentences.find(s => s.id === sentenceId)
  if (sentence) {
    sentence.target_text = newText
    sentence.is_modified = true
  }
  
  // 注意：不使用v-model或手动更新contenteditable的内容
  // 让用户直接编辑DOM，只在blur时保存
}

const handleBlur = (sentenceId: number) => {
  if (editingIndex.value < 0) return
  
  // 延迟保存，避免与点击事件冲突
  if (blurSaveTimeout.value) {
    clearTimeout(blurSaveTimeout.value)
    blurSaveTimeout.value = null
  }
  blurSaveTimeout.value = setTimeout(() => {
    if (editingIndex.value >= 0) {
      // 触发保存
      const sentence = props.sentences[editingIndex.value]
      if (sentence && sentence.is_modified) {
        saveEdit(sentenceId)
      }
    }
  }, 100)
}

const handleKeydown = (event: KeyboardEvent, sentenceId: number) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    saveEdit(sentenceId)
  } else if (event.key === 'Escape') {
    event.preventDefault()
    cancelEdit(sentenceId)
  }
}

const saveEdit = async (sentenceId: number) => {
  if (editingIndex.value < 0) return
  
  const sentence = props.sentences[editingIndex.value]
  if (!sentence) return
  
  const newText = editingElement.value?.innerText || editingElement.value?.textContent || ''
  
  console.log('saveEdit called:', {
    sentenceId,
    newText
  })
  
  // 直接更新文本和标记，不做对比
  sentence.target_text = newText
  sentence.is_modified = true
  
  // 触发保存事件
  console.log('Emitting save event:', { id: sentenceId, text: newText })
  emit('save', {
    id: sentenceId,
    text: newText
  })
  
  editingIndex.value = -1
}

const cancelEdit = (sentenceId: number) => {
  console.log('cancelEdit called for sentenceId:', sentenceId)
  
  if (editingIndex.value < 0) return
  
  // 取消任何等待中的 blur 保存，避免先触发保存再回滚
  if (blurSaveTimeout.value) {
    clearTimeout(blurSaveTimeout.value)
    blurSaveTimeout.value = null
  }

  const sentence = props.sentences[editingIndex.value]
  if (!sentence) {
    console.warn('Sentence not found at index:', editingIndex.value)
    editingIndex.value = -1
    return
  }
  
  // 恢复编辑前的原始文本
  const originalText = originalTextBeforeEdit.value
  console.log('恢复原始文本:', originalText)
  
  // 恢复文本内容
  sentence.target_text = originalText
  
  // 注意：不再重置 is_modified 状态，保持原有的编辑状态标记
  // 这样可以避免误操作导致已修改状态的丢失
  
  // 退出编辑模式
  editingIndex.value = -1
  originalTextBeforeEdit.value = ''  // 清空临时保存的文本
  
  console.log('取消编辑完成，已恢复原始文本')
  // 通知父组件（可选，用于外部状态联动）
  emit('cancel', sentenceId)
}

const saveCurrentEdit = async () => {
  if (editingIndex.value >= 0) {
    const sentence = props.sentences[editingIndex.value]
    if (sentence) {
      await saveEdit(sentence.id)
    }
  }
}

const scrollToSentence = (index: number) => {
  if (!sentencesContainer.value) return
  
  const sentenceElement = sentencesContainer.value.querySelector(`[data-sentence-id="${props.sentences[index]?.id}"]`)
  if (sentenceElement) {
    sentenceElement.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center' 
    })
  }
}

// 监听滚动事件
const handleScroll = (event: Event) => {
  const target = event.target as HTMLElement
  emit('scroll', target.scrollTop)
}

// 暴露方法给父组件
defineExpose({
  startEdit,
  saveCurrentEdit,
  scrollToSentence,
  editingIndex: computed(() => editingIndex.value)
})
</script>

<style lang="less" scoped>
.sentence-editor {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: white;
}

.loading-container,
.error-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: white;
  
  p {
    margin-top: 16px;
    color: #666;
    font-size: 14px;
  }
}

.sentences-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.sentence-pair {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: #f8f9fa;
    border-color: #e8e8e8;
  }
  
  &.highlighted {
    background: #e6f7ff;
    border-color: #91d5ff;
    box-shadow: 0 0 8px rgba(24, 144, 255, 0.3);
  }
  
  &.modified {
    border-left: 3px solid #faad14;
    background: #fffbe6;
  }
  
  &.editing {
    background: #f0f8ff;
    border-color: #1890ff;
    box-shadow: 0 0 12px rgba(24, 144, 255, 0.2);
  }
}

.sentence-number {
  width: 32px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  border-radius: 4px;
  font-size: 12px;
  color: #8c8c8c;
  margin-right: 12px;
  flex-shrink: 0;
  font-weight: 500;
}

.sentence-source {
  flex: 1;
  margin-right: 16px;
  min-width: 0;
  
  .sentence-text {
    line-height: 1.6;
    color: #262626;
    word-break: break-word;
    font-size: 14px;
  }
}

.sentence-target {
  flex: 1;
  min-width: 0;
  position: relative;
  
  .sentence-text {
    line-height: 1.6;
    color: #262626;
    word-break: break-word;
    font-size: 14px;
    min-height: 20px;
    
    &.clickable {
      cursor: pointer;
      padding: 4px 8px;
      border-radius: 4px;
      transition: background 0.2s;
      
      &:hover {
        background: #f0f8ff;
        color: #1890ff;
      }
    }
  }
  
  .sentence-editor {
    min-height: 20px;
    padding: 4px 8px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    background: white;
    outline: none;
    line-height: 1.6;
    font-size: 14px;
    color: #262626;
    word-break: break-word;
    
    &:focus {
      border-color: #1890ff;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }
    
    &:empty::before {
      content: '点击编辑译文...';
      color: #bfbfbf;
    }
  }
  
  .modification-indicator {
    position: absolute;
    top: -8px;
    right: -8px;
  }
}

.sentence-actions {
  display: flex;
  gap: 4px;
  margin-left: 8px;
  flex-shrink: 0;
  
  .ant-btn {
    width: 24px;
    height: 24px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .anticon {
      font-size: 12px;
    }
  }
}

// 滚动条样式
.sentences-container {
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .sentence-pair {
    flex-direction: column;
    gap: 8px;
  }
  
  .sentence-number {
    width: auto;
    padding: 0 8px;
  }
  
  .sentence-source,
  .sentence-target {
    margin-right: 0;
  }
  
  .sentence-actions {
    margin-left: 0;
    justify-content: flex-end;
  }
}

// 高亮动画
@keyframes highlight {
  0% {
    background: #e6f7ff;
    transform: scale(1);
  }
  50% {
    background: #bae7ff;
    transform: scale(1.02);
  }
  100% {
    background: #e6f7ff;
    transform: scale(1);
  }
}

.sentence-pair.highlighted {
  animation: highlight 0.6s ease-in-out;
}
</style>
